// Dedicated workspace for creating and editing a Spend Set.
//
// Routes:
//   /clients/:clientId/spend-sets/new          → blank spend set for clientId
//   /clients/:clientId/spend-sets/:spendSetId  → edit existing

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, DollarSign } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import { toast } from '../components/Toast';
import type { Client, Model, SpendSet } from '@rc/engine/types';
import { currencySymbol, deriveBudgetBucketId, modelCurrency } from '@rc/engine';
import { fmtMoney, fmtPct } from '../lib/format';
import type { AuthUser } from '../lib/api';
import { canDeleteClientContent } from '../lib/nbaScope';
import { parseGeoclientId, withGeoclientQuery } from '../lib/geoclientContext';

function parseNumberInput(raw: string): number {
  const cleaned = raw.replace(/[^0-9.\-]/g, '');
  const n = Number(cleaned);
  return isFinite(n) ? n : 0;
}

export default function SpendSetWorkspace({ user }: { user: AuthUser }) {
  const { clientId, spendSetId } = useParams<{ clientId?: string; spendSetId?: string }>();
  const [searchParams] = useSearchParams();
  const geoclientId = parseGeoclientId(searchParams.get('geoclientId') || searchParams.get('geoclientId'));
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [spendSet, setSpendSet] = useState<SpendSet | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isNew = !spendSetId || spendSetId === 'new';
  const canDelete = clientId ? canDeleteClientContent(user, clientId) : false;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const c = await api.getClient(clientId);
        if (cancelled) return;
        setClient(c);

        let existing: SpendSet | null = null;
        if (!isNew && spendSetId) {
          existing = await api.getSpendSet(clientId, spendSetId);
        }

        // Resolve the model to render the correct channel list.
        const modelId =
          existing?.modelId ||
          (await api.listScenarios(clientId).then((list) => list[0]?.modelId).catch(() => null));
        const models = await api.listModels();
        const resolvedModel = modelId ? models.find((m) => m.id === modelId) || models[0] : models[0];
        if (cancelled) return;
        setModel(resolvedModel || null);

        if (existing) {
          setSpendSet(existing);
        } else if (resolvedModel) {
          // Seed a blank spend set with even channel split.
          const channels = (resolvedModel.channels || []).filter((ch) => ch.active !== false);
          const evenShare = channels.length > 0 ? 1 / channels.length : 0;
          const channelAllocation: Record<string, number> = {};
          for (const ch of channels) channelAllocation[ch.id] = evenShare;
          setSpendSet({
            id: '',
            clientId,
            name: '',
            modelId: resolvedModel.id,
            totalBudget: 0,
            channelAllocation,
            channelMixPresetId: resolvedModel.channelMixPresets?.[0]?.id,
            createdAt: '',
            updatedAt: '',
          });
        }
      } catch (err) {
        if (!cancelled) toast('error', (err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, spendSetId, isNew]);

  function patch(updates: Partial<SpendSet>) {
    setSpendSet((prev) => (prev ? { ...prev, ...updates } : prev));
    setDirty(true);
  }

  async function handleSave() {
    if (!clientId || !spendSet) return;
    if (!spendSet.name.trim()) {
      toast('error', 'Spend set requires a name.');
      return;
    }
    setSaving(true);
    try {
      let saved: SpendSet;
      if (isNew) {
        saved = await api.createSpendSet(clientId, spendSet, geoclientId);
        toast('success', 'Spend set created.');
        navigate(withGeoclientQuery(`/clients/${clientId}/spend-sets/${saved.id}`, geoclientId), {
          replace: true,
        });
      } else {
        saved = await api.saveSpendSet(clientId, spendSet, geoclientId);
        toast('success', 'Spend set saved.');
      }
      setSpendSet(saved);
      setDirty(false);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!clientId || !spendSet?.id) return;
    if (!window.confirm(`Delete "${spendSet.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteSpendSet(clientId, spendSet.id, geoclientId);
      toast('success', 'Spend set deleted.');
      navigate(withGeoclientQuery(`/clients/${clientId}`, geoclientId), { replace: true });
    } catch (err) {
      toast('error', (err as Error).message);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (!spendSet || !model) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Could not load spend set.
      </div>
    );
  }

  const channels = (model.channels || []).filter((c) => c.active !== false);
  const totalBudget = Number(spendSet.totalBudget) || 0;
  const totalAlloc =
    Object.values(spendSet.channelAllocation || {}).reduce<number>(
      (s, v) => s + (Number(v) || 0),
      0,
    ) || 0;
  const allocOff = Math.abs(totalAlloc - 1) > 0.005;
  const bucketId = deriveBudgetBucketId(model, totalBudget);
  const bucket = (model.budgetBuckets || []).find((b) => b.id === bucketId);
  // Spend is entered in the bound model's currency — the engine never converts.
  const baseCurrency = modelCurrency(model).base;

  const updateBudget = (n: number) => {
    patch({ totalBudget: n });
  };

  const updatePreset = (id: string) => {
    const preset = model.channelMixPresets.find((p) => p.id === id);
    if (!preset || !preset.allocation) {
      patch({ channelMixPresetId: id });
      return;
    }
    const evenShare = channels.length > 0 ? 1 / channels.length : 0;
    const allocation: Record<string, number> = {};
    for (const ch of channels) allocation[ch.id] = preset.allocation[ch.id] ?? evenShare;
    patch({ channelMixPresetId: id, channelAllocation: allocation });
  };

  const toggleChannel = (id: string, on: boolean) => {
    patch({ activeChannels: { ...spendSet.activeChannels, [id]: on } });
  };

  const updateAllocationPct = (id: string, pct: number) => {
    const share = isFinite(pct) ? Math.max(0, pct / 100) : 0;
    patch({
      channelAllocation: { ...spendSet.channelAllocation, [id]: share },
      channelMixPresetId: 'custom',
    });
  };

  const updateAllocationDollars = (id: string, dollars: number) => {
    if (totalBudget <= 0) {
      patch({
        totalBudget: Math.max(0, dollars),
        channelAllocation: { ...spendSet.channelAllocation, [id]: dollars > 0 ? 1 : 0 },
        channelMixPresetId: 'custom',
      });
      return;
    }
    const share = Math.max(0, dollars) / totalBudget;
    patch({
      channelAllocation: { ...spendSet.channelAllocation, [id]: share },
      channelMixPresetId: 'custom',
    });
  };

  const toggleAdops = (id: string, on: boolean) => {
    patch({ adopsSupport: { ...spendSet.adopsSupport, [id]: on } });
  };

  const savedBy = spendSet.updatedBy?.displayName || spendSet.createdBy?.displayName;
  const savedAt = spendSet.updatedAt || spendSet.createdAt;

  return (
    <div className="px-8 py-8 w-full max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={withGeoclientQuery(`/clients/${clientId}`, geoclientId)}
            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 text-slate-500"
          >
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="text-xs text-slate-500 mb-0.5">
              {client?.name || clientId} / Spend sets
            </div>
            <h1 className="text-xl font-semibold text-slate-900">
              {isNew ? 'New spend set' : (spendSet.name || 'Untitled spend set')}
            </h1>
            {savedBy && savedAt && (
              <p className="text-xs text-slate-400 mt-0.5">
                Last saved by <span className="font-medium text-slate-600">{savedBy}</span>
                {' · '}
                {new Date(savedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isNew && canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="btn-secondary text-rose-600 hover:border-rose-300 hover:bg-rose-50"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            <Save size={14} />
            {saving ? 'Saving…' : dirty ? 'Save*' : 'Save'}
          </button>
        </div>
      </div>

      {/* Identity */}
      <div className="card card-body space-y-4">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            type="text"
            placeholder="e.g. FY25 Brand Media Mix"
            value={spendSet.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Notes <span className="text-slate-400 font-normal">(optional)</span></label>
          <textarea
            className="input resize-none"
            rows={2}
            placeholder="Any context about this spend set…"
            value={spendSet.notes || ''}
            onChange={(e) => patch({ notes: e.target.value })}
          />
        </div>
      </div>

      {/* Channel spend editor */}
      <div className="card card-body space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center">
            <DollarSign size={14} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Annual working media &amp; channels</h2>
            <p className="text-xs text-slate-500">Total spend and channel allocation.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">
              Annual working media
              <span className="ml-1.5 font-normal text-slate-400">({baseCurrency})</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                {currencySymbol(baseCurrency)}
              </span>
              <input
                className="input pl-9 text-right tabular-nums"
                type="text"
                inputMode="numeric"
                value={totalBudget.toLocaleString('en-US')}
                onChange={(e) => updateBudget(parseNumberInput(e.target.value))}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
              {fmtMoney(totalBudget, baseCurrency)} total
              {bucket && (
                <span className="pill bg-brand-purple-50 text-brand-purple-700 text-[10px]">
                  {bucket.label}
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="label">Channel mix preset</label>
            <select
              className="input"
              value={spendSet.channelMixPresetId || 'custom'}
              onChange={(e) => updatePreset(e.target.value)}
            >
              {(model.channelMixPresets || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="label !mb-0">Active channels &amp; allocation</span>
            <span
              className={clsx(
                'text-xs font-medium',
                allocOff ? 'text-rose-600' : 'text-emerald-600',
              )}
            >
              {fmtPct(totalAlloc, 1)} {allocOff && '(should be 100%)'}
            </span>
          </div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Channel</th>
                  <th className="text-right px-3 py-2 w-28">Share %</th>
                  <th className="text-right px-3 py-2 w-36">Dollars</th>
                  <th className="text-center px-3 py-2 w-20">AdOps</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((c) => {
                  const active = spendSet.activeChannels?.[c.id] !== false;
                  const share = spendSet.channelAllocation?.[c.id] || 0;
                  const dollars = totalBudget * share;
                  const pctValue = Number((share * 100).toFixed(2));
                  const dollarValue = Math.round(dollars);
                  return (
                    <tr
                      key={c.id}
                      className={clsx('border-t border-slate-100', !active && 'opacity-50')}
                    >
                      <td className="px-3 py-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-brand-purple-600"
                            checked={active}
                            onChange={(e) => toggleChannel(c.id, e.target.checked)}
                          />
                          <span>{c.label}</span>
                          <span
                            className={c.category === 'traditional' ? 'pill-slate' : 'pill-blue'}
                          >
                            {c.category[0]?.toUpperCase()}
                          </span>
                        </label>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="relative w-24 ml-auto">
                          <input
                            type="number"
                            step="0.1"
                            min={0}
                            max={100}
                            className="input text-right py-1 pl-2 pr-6 w-full"
                            value={pctValue}
                            onChange={(e) => updateAllocationPct(c.id, Number(e.target.value))}
                            disabled={!active}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                            %
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="relative w-36 ml-auto">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                            {currencySymbol(baseCurrency)}
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="input text-right py-1 pl-7 pr-2 w-full tabular-nums"
                            value={dollarValue.toLocaleString('en-US')}
                            onChange={(e) =>
                              updateAllocationDollars(c.id, parseNumberInput(e.target.value))
                            }
                            disabled={!active}
                            title={fmtMoney(dollars, baseCurrency)}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {c.adopsApplicable ? (
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-brand-purple-600"
                            checked={spendSet.adopsSupport?.[c.id] !== false}
                            onChange={(e) => toggleAdops(c.id, e.target.checked)}
                            disabled={!active}
                          />
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
